import { Alert, Col, message, PageHeader, Row, Select, Space, Spin, Tabs, Tag } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  ButtonItem,
  cuentaMask,
  dineroDisabledMask,
  ErrorItem,
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  TableItem,
} from "../../../../Components/Items";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { descargarExcel, descargarPdf } from "./exportarVentasPorLote";
import moment from "moment";
import "moment/locale/es";
moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

/**
 * Reporte Ventas Por Lote: una fila por Venta x Insumo x Lote entregado.
 *
 * Responde "que lote se le entrego a este cliente en esta venta". Los renglones de insumos que
 * no manejan lotes se muestran con la columna Lote en "-", para que el reporte siga cuadrando
 * contra el total de la venta.
 *
 * La tabla en pantalla lleva solo las columnas indispensables a proposito: `TableItem` fuerza
 * `tableLayout: fixed` e ignora la prop `scroll`, asi que una tabla ancha se comprime en vez de
 * desplazarse. El detalle completo va al Excel.
 */
class ReporteVentasPorLote extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    dataReporte: null,
    filas: [],
    totales: null,
    truncado: false,
    maximoFilas: 0,
    objetoClientes: null,
    objetoUsuarios: null,
    objetoProyectos: null,
    objetoUniadesDeNegocio: null,
    objetoCanalesVenta: null,
    filtrosConsultados: null,
  };

  atras() {
    this.props.history.goBack();
  }

  async componentDidMount() {
    this.setState({ loading: true });

    // Cada catalogo lleva su propio .catch(): sin el, uno que falle rechaza el Promise.all,
    // corta el componentDidMount y la consulta inicial nunca se lanza — la pantalla se queda
    // con el spinner puesto para siempre y sin ningun mensaje. Un filtro sin catalogo es una
    // degradacion aceptable; una pantalla colgada no.
    await Promise.all([
      axios
        .post(
          "clientes/selectClientes",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.status !== 404) {
            this.setState({
              objetoClientes: res.data.map((value) => (
                <Option value={value._id} key={value._id}>
                  {value.stringBuscar}
                </Option>
              )),
            });
          }
        })
        .catch(() => {}),

      axios
        .post(
          "usuarios/selectUsuariosEmpresa",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.status !== 404) {
            this.setState({
              objetoUsuarios: res.data.map((value) => (
                <Option value={value._id} key={value._id}>
                  {`${value.nombre} ${value.apellidoPaterno} ${value.apellidoMaterno}`}
                </Option>
              )),
            });
          }
        })
        .catch(() => {}),

      axios
        .post(
          "proyectos/selectProyectos",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.status !== 404) {
            this.setState({
              objetoProyectos: res.data.map((value) => (
                <Option value={value._id} key={value._id}>
                  {value.nombre}
                </Option>
              )),
            });
          }
        })
        .catch(() => {}),

      axios
        .post(
          "unidadesdenegocio/selectUnidadesDeNegocio",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.status !== 404) {
            this.setState({
              objetoUniadesDeNegocio: res.data.map((value) => (
                <Option value={value._id} key={value._id}>
                  {`${cuentaMask(
                    value.nivel1 + value.nivel2 + value.nivel3 + value.nivel4 + value.nivel5
                  )} - ${value.nombre}`}
                </Option>
              )),
            });
          }
        })
        .catch(() => {}),

      axios
        .post(
          "canalesVenta/selectCanalesVenta",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.success === 2) {
            this.setState({
              objetoCanalesVenta: (res.data.canalesVenta || []).map((value) => (
                <Option value={value._id} key={value._id}>
                  {`${value.codigo} - ${value.nombre}`}
                </Option>
              )),
            });
          }
        })
        .catch(() => {}),
    ]);

    this.consultarReporteVentasPorLote({
      periodo: [moment().subtract(1, "month"), moment()],
      clientes: 0,
      vendedores: 0,
      canalesVenta: 0,
      filtrarProyecto: 0,
      estadoFacturas: 5,
    });
  }

  consultarReporteVentasPorLote(values) {
    this.setState({ loading: true });
    axios
      .post(
        "reportesDeVentas/reporteVentasPorLote",
        { ...values, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            dataReporte: res.data,
            filas: res.data.filas || [],
            totales: res.data.totales,
            truncado: !!res.data.truncado,
            maximoFilas: res.data.maximoFilas || 0,
            filtrosConsultados: values,
          });
        } else {
          // El backend manda `message` cuando rechaza por tope de ventas: hay que mostrarlo,
          // es lo que le dice al usuario que acote el periodo.
          message.error(
            res.data?.message || "Ha sucedido un error y no se cargo bien el reporte"
          );
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Error de conexión");
        this.setState({ loading: false });
      });
  }

  /** Resumen legible de los filtros, para el encabezado del Excel y del PDF. */
  descripcionFiltros() {
    const filtros = this.state.filtrosConsultados;
    if (!filtros) return "";

    const partes = [];
    if (filtros.periodo?.length === 2) {
      partes.push(
        `Del ${moment(filtros.periodo[0]).format("DD/MM/YYYY")} al ${moment(
          filtros.periodo[1]
        ).format("DD/MM/YYYY")}`
      );
    }
    const ETIQUETA_ESTADO = {
      5: "Ventas activas",
      4: "Por cobrar",
      3: "Cobradas",
      2: "Vencidas",
      1: "Canceladas",
      0: "Activas y canceladas",
    };
    if (ETIQUETA_ESTADO[filtros.estadoFacturas]) {
      partes.push(ETIQUETA_ESTADO[filtros.estadoFacturas]);
    }
    if (filtros.clientes?.length > 0) partes.push(`${filtros.clientes.length} cliente(s)`);
    if (filtros.vendedores?.length > 0) partes.push(`${filtros.vendedores.length} vendedor(es)`);
    if (filtros.canalesVenta?.length > 0) {
      partes.push(`${filtros.canalesVenta.length} canal(es) de venta`);
    }
    if (filtros.filtrarProyecto === 1) partes.push("Filtrado por proyecto");
    if (filtros.filtrarProyecto === 2) partes.push("Filtrado por unidad de negocio");

    return partes.join(" - ");
  }

  parametrosExportacion() {
    return {
      datos: this.state.filas,
      totales: this.state.totales,
      empresaNombre: this.props.empresa,
      descripcionFiltros: this.descripcionFiltros(),
    };
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  render() {
    const columns = [
      {
        title: "Folio",
        dataIndex: "folio",
        key: "folio",
        buscar: "folio",
        width: "9%",
        render: (value, record) => (
          <>
            {value}
            {record.cancelada && (
              <Tag color="red" style={{ marginLeft: 4 }}>
                Cancelada
              </Tag>
            )}
          </>
        ),
      },
      { title: "Fecha", dataIndex: "fecha", key: "fecha", width: "7%" },
      { title: "Cliente", dataIndex: "cliente", key: "cliente", buscar: "cliente", width: "15%" },
      { title: "Clave", dataIndex: "clave", key: "clave", buscar: ["clave", "codigo"], width: "8%" },
      {
        title: "Descripcion",
        dataIndex: "descripcion",
        key: "descripcion",
        buscar: "descripcion",
        width: "18%",
      },
      { title: "Lote", dataIndex: "lote", key: "lote", buscar: "lote", width: "10%" },
      { title: "Caducidad", dataIndex: "caducidad", key: "caducidad", width: "8%" },
      {
        title: "Cantidad",
        dataIndex: "cantidad",
        key: "cantidad",
        width: "7%",
        align: "right",
        sorter: (a, b) => a.cantidad - b.cantidad,
      },
      {
        title: "P.U.",
        dataIndex: "precioUnitario",
        key: "precioUnitario",
        width: "9%",
        align: "right",
        render: (value) => dineroDisabledMask(value),
      },
      {
        title: "Importe",
        dataIndex: "importe",
        key: "importe",
        width: "9%",
        align: "right",
        sorter: (a, b) => a.importe - b.importe,
        render: (value) => dineroDisabledMask(value),
      },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteVentasPorLote"
          onFinish={this.consultarReporteVentasPorLote.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().subtract(1, "month"), moment()],
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Reporte Ventas Por Lote"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {this.state.filas.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => descargarExcel(this.parametrosExportacion())}
                      icon={<FileExcelOutlined />}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  {this.state.filas.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => descargarPdf(this.parametrosExportacion())}
                      icon={<FilePdfOutlined />}
                    >
                      Descargar PDF
                    </ButtonItem>
                  )}
                  <ButtonItem type="primary" htmlType="submit">
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={this.state.loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Clientes">
                  <FormItem name="clientes" noStyle margin>
                    <SelectItem width="90%" mode="multiple" placeholder="Clientes">
                      {this.state.objetoClientes}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Vendedores">
                  <FormItem name="vendedores" margin noStyle>
                    <SelectItem width="90%" mode="multiple" placeholder="Vendedores">
                      {this.state.objetoUsuarios}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Proyecto">
                  <Row>
                    <Col span={8}>
                      <FormItem name="filtrarProyecto" required noStyle initialValue={0}>
                        <SelectItem width="90%" placeholder="Filtrar Proyecto">
                          <Option default value={0}>
                            Ninguno
                          </Option>
                          <Option value={1}>Proyectos</Option>
                          <Option value={2}>Unidades de negocio</Option>
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col span={8}>
                      <FormItem shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          const filtrarProyecto = getFieldValue("filtrarProyecto");
                          if (filtrarProyecto === 1) {
                            return (
                              <FormItem required name="proyectos" margin noStyle>
                                <SelectItem width="90%" placeholder="Proyectos" mode="multiple">
                                  {this.state.objetoProyectos}
                                </SelectItem>
                              </FormItem>
                            );
                          }
                          if (filtrarProyecto === 2) {
                            return (
                              <FormItem required margin name="unidadDeNegocioId" noStyle>
                                <SelectItem width="90%" placeholder="Unidades de Negocio">
                                  {this.state.objetoUniadesDeNegocio}
                                </SelectItem>
                              </FormItem>
                            );
                          }
                          return null;
                        }}
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabKey="5" key="5" forceRender tab="Canales de Venta">
                  <FormItem name="canalesVenta" margin noStyle>
                    <SelectItem width="90%" mode="multiple" placeholder="Canales de Venta">
                      {this.state.objetoCanalesVenta}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="6" key="6" forceRender tab="Opciones">
                  <Row style={{ padding: 0, margin: 0 }} gutter={[16, 0]}>
                    <Col span={12}>
                      <FormItem
                        label="Estado de las Ventas"
                        name="estadoFacturas"
                        required
                        initialValue={5}
                        style={{ marginBottom: 0 }}
                      >
                        <SelectItem placeholder="Filtrar Por">
                          <Option default value={5}>
                            Activas
                          </Option>
                          <Option value={4}>Por Cobrar</Option>
                          <Option value={3}>Cobrada</Option>
                          <Option value={2}>Vencida</Option>
                          <Option value={1}>Canceladas</Option>
                          <Option value={0}>Todas (Activas y Canceladas)</Option>
                        </SelectItem>
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
              </Tabs>
            </div>

            {this.state.truncado && (
              <Alert
                style={{ marginTop: 10 }}
                type="warning"
                showIcon
                message={`El reporte se corto en ${this.state.maximoFilas.toLocaleString(
                  "es-MX"
                )} renglones. Acota el periodo o agrega filtros para verlo completo.`}
              />
            )}

            {this.state.totales && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 15,
                  marginTop: 10,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <Row gutter={[16, 8]}>
                  <Col span={6}>
                    <div style={{ color: "#8c8c8c", fontSize: 12 }}>Renglones</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>
                      {this.state.totales.filas.toLocaleString("es-MX")}
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ color: "#8c8c8c", fontSize: 12 }}>Ventas</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>
                      {this.state.totales.ventas.toLocaleString("es-MX")}
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ color: "#8c8c8c", fontSize: 12 }}>Lotes distintos</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>
                      {this.state.totales.lotesDistintos.toLocaleString("es-MX")}
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ color: "#8c8c8c", fontSize: 12 }}>Importe</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: "#00BFD6" }}>
                      {dineroDisabledMask(this.state.totales.importe)}
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <TableItem
                onRow={(record) => ({
                  onDoubleClick: () =>
                    this.props.history.push("/panel/verVenta", { id: record.ventaId }),
                })}
                rowKey={(value) => value._id}
                columns={columns}
                dataSource={this.state.filas}
                size="small"
                defaultPageSize={50}
              />
            </div>
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(ReporteVentasPorLote);
